/* eslint-disable consistent-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { getArrFromLocalStorage, setArrToLocalStorage } from '@vlabs/monokit/shared/lib/localStorage';
import { PlusIcon, NotificationOffIcon } from '@vlabs/icons';
import { routes } from '@vlabs/shared/navigation/routes';
import { actionColumnProps } from '@vlabs/shared/utils';
import { Panel, Table, Control, Dropdown, DropdownItem, useTableHook } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  NotificationOption,
  EditNotificationCell,
  DuplicateNotificationCell,
  DeleteNotificationCell,
  TelegramNotificationStatusButton,
  NotificationStatus,
} from './Cell';
import st from './NotificationListPage.module.sass';
import { selectNotifications } from './selectors';
import { updateNotification, updatePageSize, updatePage } from './store';
import { Page, PageContent, PageHeader } from '@vlabs/monokit/shared/ui/page';
import classNames from 'classnames';

const NotificationListPageComponent = ({ notificationsData, changeEnable, setPageSize, setPageIndex }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [isOpen, setIsOpen] = useState(false);

  const tableProps = useTableHook(useCallback(async () => { return notificationsData; }, [notificationsData.data]));

  const [pausedNotificationArr, setPausedNotificationArr] = useState(getArrFromLocalStorage('pausedNotifications'));
  const [vaNotificationsArr, setVANotificationsArr] = useState(getArrFromLocalStorage('VANotificationsArr'));

  const data = [...notificationsData.data, ...vaNotificationsArr];

  useEffect(() => {
    setArrToLocalStorage('pausedNotifications', pausedNotificationArr);
    tableProps.onFetch();
  }, [pausedNotificationArr.length]);

  useEffect(() => {
    const arr = getArrFromLocalStorage('VANotificationsArr');
    setVANotificationsArr(arr);
  }, [localStorage.getItem('VANotificationsArr')]);

  useEffect(() => {
    const panel = document.querySelector(`.${st.CustomPanel}`);
    const headerWrapper = document.querySelector('.PageHeaderWrapper');
    const scrollContainer = document.querySelector('.Table__ScrollContainer');
    let prevPageYOffset = 0;

    function hideOrVisibleHeader() {
      const currentPageYOffset = scrollContainer.scrollTop;
      const startTrigger = currentPageYOffset >= 100;
      const isScrollingDown = currentPageYOffset > prevPageYOffset;

      headerWrapper.classList.toggle(st.HeaderHide, isScrollingDown && startTrigger);
      panel.classList.toggle(st.CustomPanel_headerHidden, isScrollingDown && startTrigger);

      if (!startTrigger) {
        headerWrapper.classList.remove(st.HeaderHide);
        panel.classList.remove(st.CustomPanel_headerHidden);
      }

      prevPageYOffset = currentPageYOffset;
    }

    scrollContainer.addEventListener('scroll', hideOrVisibleHeader);
    return () => scrollContainer.removeEventListener('scroll', hideOrVisibleHeader);
  }, []);

  const changeTelegramEnable = useCallback((value) => {
    const newData = {
      ...value,
      email: {
        ...value.email,
        recipients: value.email.recipients.join(','),
      },
      telegram: {
        ...value.telegram,
        enable: !value.telegram.enable,
        recipients: value.telegram.recipients.join(','),
      },
    };
    changeEnable(value.uuid, newData);
  }, []);

  const onPauseNotification = (notification, type, time) => {
    const id = notification.uuid;
    if (type === 'add') {
      setPausedNotificationArr((prev) => {
        if (prev.some((n) => n.id === id)) return prev;
        return [...prev, { id, time }];
      });
      tableProps.onReset();
    } else if (type === 'delete') {
      if (localStorage.getItem('offUINotification')) localStorage.removeItem('offUINotification');
      setPausedNotificationArr((prev) => prev.filter((n) => n.id !== id));
      tableProps.onReset();
    }
  };

  const changeUIEnable = (value) => {
    if (value.filters?.type?.value === 'videoanalytics') {
      if (pausedNotificationArr.find((n) => n.id === value.uuid)) {
        onPauseNotification(value, 'delete');
      }
      const newArr = vaNotificationsArr.map((va) => (va.uuid === value.uuid
        ? { ...va, ui: { ...va.ui, enable: !va.ui.enable } }
        : va),
      );
      setArrToLocalStorage('VANotificationsArr', newArr);
      setVANotificationsArr(newArr);
      toast.success(t('vlNotifier:update.success'));
    } else {
      const newData = {
        ...value,
        email: {
          ...value.email,
          recipients: value.email.recipients.join(','),
        },
        telegram: {
          ...value.telegram,
          enable: value.telegram.enable,
          recipients: value.telegram.recipients.join(','),
        },
        ui: {
          enable: !value.ui.enable,
        },
      };
      if (pausedNotificationArr.find((n) => n.id === value.uuid)) {
        onPauseNotification(value, 'delete');
      }
      changeEnable(value.uuid, newData);
    }
  };

  const disableAllNotificationsCompletely = useCallback(async () => {
    const vaDataArr = data.filter((n) => n.uuid.startsWith('va')) || [];
    const eventDataArr = data.filter((n) => !n.uuid.startsWith('va')) || [];

    if (vaDataArr.length) {
      const enableUIArr = [...vaNotificationsArr].map((va) => ({
        ...va,
        ui: { ...va.ui, enable: false },
      }));

      setArrToLocalStorage('VANotificationsArr', enableUIArr);
    }

    if (eventDataArr.length) {
      await eventDataArr.reduce((promise, value) => {
        return promise.then(async () => {
          const newData = {
            ...value,
            email: {
              ...value.email,
              recipients: value.email?.recipients?.join(','),
            },
            telegram: {
              ...value.telegram,
              recipients: value.telegram.recipients.join(','),
            },
            ui: { enable: false },
          };

          try {
            await changeEnable(value.uuid, newData);
          } catch (err) {
            return Promise.reject(err);
          }
        });
      }, Promise.resolve());
    }
  }, [data, vaNotificationsArr, changeEnable]);

  const disableAllNotificationsTemporarily = useCallback((pauseTime) => {
    setPausedNotificationArr((prev) => {
      const newPausedList = data.map((n) => {
        const existing = prev.find((item) => item.id === n.uuid);
        return existing
          ? { ...existing, time: pauseTime }
          : { id: n.uuid, time: pauseTime };
      });

      return newPausedList;
    });

    localStorage.setItem('offUINotification', pauseTime);
  }, [data]);

  const offAllNotification = useCallback(async (time) => {
    const pauseTime = time === Infinity ? Infinity : Date.now() + time;

    if (pauseTime === Infinity) {
      await disableAllNotificationsCompletely();
    } else {
      disableAllNotificationsTemporarily(pauseTime);
    }
  }, [disableAllNotificationsCompletely, disableAllNotificationsTemporarily]);

  const actions = (
    <div className={st.Actions}>
      <Control.Button
        data-testid="addNotificationButton"
        icon={<PlusIcon />}
        onClick={() => history.push(
          generatePath(routes.notifications.create),
        )}
      >
        {t('btn.add')}
      </Control.Button>
      <Dropdown
        className={st.DropDownContent}
        options={[
          <DropdownItem key="0" label={t('vlNotifier:time.15_minutes')} onClick={() => { offAllNotification(15 * 60 * 1000); setIsOpen(!isOpen); }} />,
          <DropdownItem key="1" label={t('vlNotifier:time.30_minutes')} onClick={() => { offAllNotification(30 * 60 * 1000); setIsOpen(!isOpen); }} />,
          <DropdownItem key="4" label={t('vlNotifier:time.1_hour')} onClick={() => { offAllNotification(60 * 60 * 1000); setIsOpen(!isOpen); }} />,
          <DropdownItem key="2" label={t('vlNotifier:time.2_hours')} onClick={() => { offAllNotification(120 * 60 * 1000); setIsOpen(!isOpen); }} />,
          <DropdownItem key="3" label={t('vlNotifier:time.6_hours')} onClick={() => { offAllNotification(360 * 60 * 1000); setIsOpen(!isOpen); }} />,
          <DropdownItem key="5" label={t('vlNotifier:time.forever')} onClick={() => { offAllNotification(Infinity); setIsOpen(!isOpen); }} />,
        ]}
        isOpen={isOpen}
      >
        <Control.Button
          data-testid="offAllNotifications"
          icon={<NotificationOffIcon />}
          kind="negative"
          onClick={() => setIsOpen(!isOpen)}
          variant="filled"
        >
          {t('btn.disable_all')}
        </Control.Button>
      </Dropdown>
    </div>
  );

  const columns = useMemo(() => (
    [
      { Header: t('name'), id: 'name', accessor: 'filters.name' },
      {
        Header: t('vlNotifier:columns.event_type'),
        id: 'type',
        accessor: (row) => {
          return row.filters?.type
            ? t('vlNotifier:type.videoanalytics')
            : t('vlNotifier:type.detection_and_match');
        },
      },
      {
        Header: t('vlNotifier:columns.creation_rules'),
        id: 'rules',
        accessor: (row) => row.filters,
        Cell: NotificationOption,
      },
      {
        Header: t('vlNotifier:telegram.enable'),
        id: 'telegram',
        accessor: (row) => ({
          enable: row.telegram?.enable,
          onClick: () => changeTelegramEnable(row),
        }),
        Cell: TelegramNotificationStatusButton,
      },
      {
        Header: t('vlNotifier:ui.enable'),
        id: 'ui',
        accessor: (row) => ({
          notification: row,
          pausedNotification: pausedNotificationArr.find((n) => n.id === row.uuid),
          isEnable: row.ui?.enable,
          onClick: () => changeUIEnable(row),
          onPause: onPauseNotification,
        }),
        Cell: NotificationStatus,
      },
      actionColumnProps({
        id: 'edit',
        accessor: (row) => ({
          id: row.uuid,
          type: row.filters?.type,
        }),
        Cell: EditNotificationCell,
      }),
      actionColumnProps({
        id: 'duplicate',
        accessor: (row) => row,
        Cell: DuplicateNotificationCell,
      }),
      actionColumnProps({
        id: 'delete',
        accessor: (row) => ({
          name: row.filters?.name,
          uuid: row.uuid,
          type: row.filters?.type?.value || 'events',
          reset: tableProps.onReset,
        }),
        Cell: DeleteNotificationCell,
      }),
    ]
  ), [
    t,
    pausedNotificationArr.length,
    vaNotificationsArr.length,
    changeUIEnable,
    onPauseNotification,
    changeTelegramEnable,
  ]);

  return (
    <Page>
      <PageContent>
      <PageHeader
        actions={actions}
        title={t('vlNotifier:page.title')}
      />
      <Panel className={classNames(st.CustomPanel, 'PanelAutoHeight')}>
        <Table
          columns={columns}
          data={data}
          disableSortBy={false}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
        />
      </Panel>
      </PageContent>
    </Page>
  );
};

export const NotificationListPage = connect(
  (state) => ({
    notificationsData: selectNotifications(state),
  }),
  (dispatch) => ({
    changeEnable: (id, data) => dispatch(updateNotification(id, data)),
    setPageSize: (pageSize) => dispatch(updatePageSize({ pageSize })),
    setPageIndex: (pageIndex) => dispatch(updatePage({ pageIndex })),
  }),
)(NotificationListPageComponent);
